import { useCallback, useRef, useState } from 'react';

export type AiTimelineMessage = {
  id: string;
  role: 'USER' | 'ASSISTANT' | 'SYSTEM';
  content: string;
  timestamp: string;
  isStreaming?: boolean;
};

type UseAiSchedulingStreamOptions = {
  nodeServiceUrl: string;
  orgId: string;
  taskId: string;
  actorEmployeeId: string;
  applicationId: string;
  authToken?: string;
};

/**
 * Hook that streams from the Node scheduling agent endpoint.
 * Returns messages as they arrive via SSE text stream.
 */
export function useAiSchedulingStream({
  nodeServiceUrl,
  orgId,
  taskId,
  actorEmployeeId,
  applicationId,
  authToken,
}: UseAiSchedulingStreamOptions) {
  const [messages, setMessages] = useState<AiTimelineMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const sendMessage = useCallback(
    async (requestText: string) => {
      // Add user message immediately
      const userMessage: AiTimelineMessage = {
        id: `user-${Date.now()}`,
        role: 'USER',
        content: requestText,
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMessage]);
      setIsStreaming(true);

      // Create streaming assistant message
      const assistantId = `assistant-${Date.now()}`;
      setMessages((prev) => [
        ...prev,
        {
          id: assistantId,
          role: 'ASSISTANT',
          content: '',
          timestamp: new Date().toISOString(),
          isStreaming: true,
        },
      ]);

      try {
        abortRef.current = new AbortController();

        const response = await fetch(`${nodeServiceUrl}/api/ai/scheduling/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(authToken ? { Authorization: authToken } : {}),
          },
          body: JSON.stringify({
            orgId,
            taskId,
            actorEmployeeId,
            applicationId,
            requestText,
          }),
          signal: abortRef.current.signal,
        });

        if (!response.ok || !response.body) {
          throw new Error(`Stream failed: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let fullContent = '';

        for (let done = false; !done;) {
          const result = await reader.read(); // eslint-disable-line no-await-in-loop
          done = result.done;
          if (done) {
            break;
          }

          const chunk = decoder.decode(result.value, { stream: true });
          fullContent += chunk;

          const captured = fullContent;
          setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, content: captured } : m)));
        }

        // Mark as done streaming
        setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, isStreaming: false } : m)));
      } catch (error) {
        if ((error as Error).name === 'AbortError') {
          return;
        }

        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId ? { ...m, content: 'An error occurred. Please try again.', isStreaming: false } : m
          )
        );
      } finally {
        setIsStreaming(false);
        abortRef.current = null;
      }
    },
    [nodeServiceUrl, orgId, taskId, actorEmployeeId, applicationId, authToken]
  );

  const stopStreaming = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  return { messages, isStreaming, sendMessage, stopStreaming };
}
